import { createContext, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';

export type NavScrollContextValue = {
  scrollRef: React.RefObject<ScrollView | null>;
  viewportWidth: number;
  contentWidth: number;
};

export const NavScrollContext = createContext<NavScrollContextValue | null>(
  null,
);

const WrappedScrollView = withUnistyles(ScrollView);

function NavRenderedItem<T>({
  item,
  index,
  renderItem,
}: {
  item: T;
  index: number;
  renderItem: (info: { item: T; index: number }) => React.ReactNode;
}) {
  return <>{renderItem({ item, index })}</>;
}

export default function Nav<T>(props: {
  data: readonly T[];
  keyExtractor: (item: T, index: number) => string;
  renderItem: (info: { item: T; index: number }) => React.ReactNode;
  ref?: React.Ref<ScrollView | null>;
}) {
  const { data, keyExtractor, renderItem, ref } = props;
  const [viewportWidth, setViewportWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const scrollRef = ref as React.RefObject<ScrollView | null>;

  const contextValue = {
    scrollRef,
    viewportWidth,
    contentWidth,
  };

  if (data.length < 2) {
    return null;
  }

  return (
    <NavScrollContext.Provider value={contextValue}>
      <WrappedScrollView
        ref={ref}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={style.scrollView}
        contentContainerStyle={style.container}
        onLayout={(event) => {
          setViewportWidth(event.nativeEvent.layout.width);
        }}
        onContentSizeChange={(width) => {
          setContentWidth(width);
        }}
      >
        <View style={style.row}>
          {data.map((item, index) => (
            <NavRenderedItem
              key={keyExtractor(item, index)}
              item={item}
              index={index}
              renderItem={renderItem}
            />
          ))}
        </View>
      </WrappedScrollView>
    </NavScrollContext.Provider>
  );
}

const style = StyleSheet.create((theme) => ({
  scrollView: {
    flexGrow: 0,
    flexShrink: 0,
  },
  container: {
    paddingHorizontal: theme.size(4),
    paddingBottom: theme.size(2),
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
}));
